import React, { useEffect, useState } from 'react';
import { DatePicker } from "antd"
import dayjs from "dayjs";
import Button from '../../components/Button/Button';
import { getPassportById, updatePassportById } from '../../services/studentApi';
import { passport } from '../../utils/constant';
import { AiOutlineLoading } from "react-icons/ai";
import useFormValidation from '../../hooks/useFormValidation';
import { passportFormValidationSchema } from '../../utils/formValidation';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { Trans } from 'react-i18next';
import { Link } from 'react-router-dom';
import { errorToast } from '../../utils/errorHandling';

const base_url = process.env.REACT_APP_BASE_URL;

const ViewPassportInfo = () => {
    const { t } = useTranslation();
    const passportCopy = passport(t);
    const [originalData, setOriginalData] = useState({});
    const [formData, setFormData] = useState("");
    const [isLoading, setIsLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const { errors, validateField, validateAllFields } = useFormValidation(passportFormValidationSchema(t));
    const [passportRegion, setPassportRegion] = useState(null);
    const [formValidationSchema, setFormValidationSchema] = useState();
    const [passportExpiryDate, setPassportExpiryDate] = useState(null);
    const [passportExpired, setPassportExpired] = useState(null);
    const [courseData, setCourseData] = useState(null);
    const [previews, setPreviews] = useState(null);
    const lang = localStorage.getItem("i18nextLng");

    const fetchActiveCourse = async () => {
        try {
            const res = localStorage.getItem("courseData")
                ? JSON.parse(localStorage.getItem("courseData"))
                : null;
            setCourseData(res);
            if (res !== null) {
                const newDate = dayjs(res.start_datetime).add(6, 'month');
                setPassportExpiryDate(newDate);
            }
        } catch (error) {
            console.log(error);
        }
    };

    const fetchPassport = async () => {
        try {
            const data = await getPassportById();
            if (data) {
                setFormData({
                    "ic_no": data.ic_no || "",
                    "passport_expired_date": data.passport_expired_date || "",
                    "passport_image": data.passport_image,
                    "passport_no": data.passport_no || "",
                    "passport_region": data.passport_region || "",
                });
                setPassportRegion(data.passport_region);
                setOriginalData(data);
            }
        } catch (error) {
            throw (error);
        }
    };

    useEffect(() => {
        const passportApiSeries = async () => {
            try {
                setIsLoading(true);
                await fetchPassport();
                await fetchActiveCourse();
            } catch (error) {
                errorToast(error, t('general.ActionFailed'), t);
            } finally {
                setIsLoading(false);
            }
        }
        passportApiSeries()
    }, []);

    useEffect(() => {
        if (passportRegion === "Malaysia") {
            setFormValidationSchema("ic_no", "passport_region");
        } else {
            setFormValidationSchema(
                Object.keys(formData).filter((item) => (
                    !["ic_no", "passport_image"].includes(item)
                ))
            );
        }
    }, [passportRegion]);

    const handleDateChange = (e) => {
        setFormData((prevState) => ({
            ...prevState,
            "passport_expired_date": e,
        }));
        validateField("passport_expired_date", e);
        if (e && e < passportExpiryDate) {
            setPassportExpired(t("FormValid.passportExpired"))
        } else {
            setPassportExpired(null);
        }
    };

    const handleInputChange = (e) => {
        const { id, value } = e.target;
        if (id === "passport_region") {
            if (value === "Malaysia") {
                setFormData((prevState) => ({
                    ...prevState,
                    passport_no: "",
                    passport_expired_date: "",
                    passport_image: "",
                    [id]: value,
                }));
                validateField(id, value);
            } else {
                setFormData((prevState) => ({
                    ...prevState,
                    ic_no: "",
                    [id]: value,
                }));
                validateField(id, value);
            }
        } else {
            setFormData((prevState) => ({
                ...prevState,
                [id]: value,
            }));
            validateField(id, value);
        }
    };

    const [fileError, setFileError] = useState(null);
    const maxSize = 10 * 1024 * 1024; // 10MB in bytes
    const handleFileChange = (event) => {
        const { id } = event.target;
        const selectedFile = event.target.files[0];
        const allowedTypes = [
            "image/jpeg",
            "image/png",
            "application/pdf",
            "image/jpg",
            "image/heic"
        ];
        if (selectedFile && !allowedTypes.includes(selectedFile.type)) {
            setFileError(t("FormValid.FileExtensionAllow"));
            event.target.value = ""; // Clear the file input
            setPreviews(null);
            return;
        } else if (selectedFile && selectedFile.size > maxSize) {
            setFileError(t("FormValid.smallerFile10MB"));
            event.target.value = ""; // Clear the file input
            setPreviews(null);
            return;
        }
        setFileError(null);
        setFormData((prevState) => ({
            ...prevState,
            [id]: selectedFile,
        }));
        // set previews
        if (selectedFile && selectedFile.type.startsWith("image/")) {
            const previewUrl = URL.createObjectURL(selectedFile);
            setPreviews(previewUrl);
        } else {
            // If PDF or non-image → no preview
            setPreviews(null);
        }
        validateField(id, selectedFile);
    };

    const handleCancel = () => {
        setFormData({
            "ic_no": originalData.ic_no || "",
            "passport_expired_date": originalData.passport_expired_date || "",
            "passport_image": "",
            "passport_no": originalData.passport_no || "",
            "passport_region": originalData.passport_region || "",
        });
        setIsEditing(false);
        setPreviews(null);
    };


    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        if (validateAllFields(formData, formValidationSchema) && fileError === null && passportExpired === null) {
            try {
                const payload = {
                    ...formData,
                    passport_expired_date: dayjs(formData.passport_expired_date).format("YYYY-MM-DD"),
                };
                const response = await updatePassportById(payload);
                if (response) {
                    setIsEditing(false);
                    toast.success(t("Passport.SubmitedSuccess"));
                    fetchPassport();
                }
            } catch (error) {
                errorToast(error, t("Passport.SubmitedFailed"), t);
            } finally {
                setIsSubmitting(false);
            }
        } else {
            toast.error(t("general.ValidateFailed"));
            setIsSubmitting(false);
        }
    };

    return (
        <>
            {formData === "" ?
                <div className='info-container'>
                    <h3>{t("Passport.pTitle")}</h3>
                    {isLoading ?
                        <>
                            <AiOutlineLoading className='spin' />
                            <p>{t("Passport.Loading")}</p>
                        </>
                        :
                        <>
                            <p style={{ "color": "#ff0000" }}><Trans key="accomodation" html="br:<br/>">
                                <br />{t("Passport.warning")}
                            </Trans></p>
                            <div className='action-buttons'>
                                <Link to={`${base_url}/submit-passport`}>
                                    <Button
                                        buttonStyle='btn--primary'
                                        buttonSize='btn--small'
                                    >{t("Passport.submitPassport")}</Button>
                                </Link>

                            </div>
                        </>
                    }

                </div>
                :
                <>
                    <div className='info-container'>
                        <h3>{t("Passport.pTitle")}</h3>
                        <p>{lang == 'en' ? courseData.course_ename : courseData.course_cname}</p>
                        <p className='error text-center'>{t("Passport.CompleteForm")}</p>
                        {!isEditing ?
                            <div className='action-buttons'>
                                <Button
                                    buttonStyle='btn--primary'
                                    buttonSize='btn--small'
                                    onClick={() => setIsEditing(true)}
                                >{t("Passport.Editpassport")}</Button>
                            </div>
                            :
                            <div className='action-buttons'>
                                <Button buttonStyle='btn--primary' disabled={isSubmitting} onClick={handleCancel} type="button">{t("general.BTNcancel")}</Button>
                            </div>
                        }
                    </div>
                    <div className='form-wrapper'>
                        <form onSubmit={handleSubmit}>
                            <div className="form-item">
                                <label for='passport_region'>{t("Passport.Region_pass")}
                                    <span style={{ "color": "#ff0000" }}>*</span></label>
                                <select disabled={!isEditing} className="form-select" id='passport_region' value={formData.passport_region || ""}
                                    onChange={(e) => { setPassportRegion(e.target.value); handleInputChange(e) }}
                                >
                                    <option value="">{t("Passport.Select_reg")}</option>
                                    {
                                        passportCopy.map((i, index) => (
                                            <option key={index} value={i.key}>{i.country}</option>
                                        ))
                                    }
                                </select>
                            </div>
                            {errors.passport_region && <span className="error">{errors.passport_region}</span>}
                            {
                                formData.passport_region === "Malaysia" &&
                                <>
                                    <div className="form-item">
                                        <label for="ic_no" className="form-label">{t("Passport.ICnum")}<span style={{ "color": "#ff0000" }}>*</span></label>
                                        <div className="form-info mt-2">
                                            <p className='mb-0'>{t("Passport.fillIC")}</p>
                                        </div>
                                        <input disabled={!isEditing} type="text" className="form-control" id="ic_no" placeholder="790111-10-1234" onChange={handleInputChange} value={formData.ic_no || ""} required />
                                    </div>
                                    {errors.ic_no && <span className="error">{errors.ic_no}</span>}
                                </>
                            }
                            {
                                formData.passport_region !== "Malaysia" &&
                                <>
                                    <div className="form-item">
                                        <label for="passport_no" className="form-label">{t("Passport.passNUM")}<span style={{ "color": "#ff0000" }}>*</span></label>
                                        <input disabled={!isEditing} type="text" className="form-control" id="passport_no" placeholder="" onChange={handleInputChange} value={formData.passport_no || ""} required />
                                    </div>
                                    {errors.passport_no && <span className="error">{errors.passport_no}</span>}
                                    <div className="form-item">
                                        <label for="passport_expired_date" className="form-label">{t("Passport.passEXPIRED")}<span style={{ "color": "#ff0000" }}>*</span></label>
                                        <DatePicker
                                            disabled={!isEditing}
                                            placeholder={t("general.date")}
                                            value={formData.passport_expired_date ? dayjs(formData.passport_expired_date) : null}
                                            onChange={handleDateChange}
                                            inputReadOnly={true}
                                        />
                                    </div>
                                    {passportExpired && <span className="error">{passportExpired}</span>}
                                    {errors.passport_expired_date && <span className="error">{errors.passport_expired_date}</span>}
                                    <div className="form-item">
                                        <label for="passport_image" className="form-label">
                                            {t("Passport.frontIMG")}
                                            <span style={{ "color": "#ff0000" }}>*</span></label>
                                        <p className='mb-0'>{t("Passport.myIMG")}</p>
                                        <div className='form-info'><span style={{ "color": "red" }}>{t("Passport.imageInfo")}</span></div>
                                        {
                                            previews ?
                                                <img src={previews} className="img-thumbnail mt-3" style={{ maxWidth: "200px" }} />
                                                :
                                                <img src={`${originalData.passport_image}?_=${Date.now()}`} alt="" style={{ "width": "200px" }} />
                                        }
                                        <input
                                            disabled={!isEditing}
                                            type="file"
                                            className="form-control mt-2"
                                            onChange={handleFileChange}
                                            id="passport_image"
                                        />
                                    </div>
                                    <div className="form-info mt-2">
                                        <p className='mb-0'>{t("general.smallerFile")}</p>
                                    </div>
                                    {fileError && <span className="error">{fileError}</span>}
                                    {errors.passport_image && <span className="error">{errors.passport_image}</span>}
                                </>
                            }
                            {isEditing &&
                                <div className='submit-button d-flex justify-content-center mt-4 gap-3'>
                                    <Button buttonStyle='btn--primary' disabled={isSubmitting} type="submit">
                                        {isSubmitting ? <AiOutlineLoading className="spin" /> : t("general.BTNsave")}
                                    </Button>
                                </div>
                            }
                        </form>
                    </div>
                </>
            }

        </>
    )
}

export default ViewPassportInfo